import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import * as Icons from 'lucide-react';
import { ArrowLeft, ArrowRight, Sparkles, CheckCircle2 } from 'lucide-react';
import { useQuest } from '@/context/QuestContext';
import { QUESTIONS, LEVELS } from '@/lib/quizData';
import { fireCenterPop } from '@/lib/confetti';
import { track } from '@/lib/api';
import { BrandBanner } from '@/components/BrandLogo';

export default function Quiz() {
  const navigate = useNavigate();
  const { session, setSession } = useQuest();
  const [idx, setIdx] = useState(0);
  const [direction, setDirection] = useState(1);
  const [showLevelUp, setShowLevelUp] = useState(null);
  const [pulse, setPulse] = useState(false);

  useEffect(() => {
    if (!session.player) navigate('/setup');
  }, [session.player, navigate]);

  const q = QUESTIONS[idx];
  const current = useMemo(() => LEVELS.find((l) => idx + 1 >= l.range[0] && idx + 1 <= l.range[1]) || LEVELS[0], [idx]);
  const progress = ((idx) / QUESTIONS.length) * 100;

  const pick = (option) => {
    if (showLevelUp || pulse) return;
    const next = [...session.answers.filter((a) => a.question_id !== q.id), { question_id: q.id, option_id: option.id }];
    setSession({ ...session, answers: next });
    track('quiz_answer', { question_id: q.id, option: option.id });

    setPulse(true);
    const isLastOfLevel = idx + 1 === current.range[1];

    setTimeout(() => {
      setPulse(false);
      if (isLastOfLevel) {
        fireCenterPop();
        setShowLevelUp(current);
        track('level_completed', { level: current.id });
        return;
      }
      if (idx + 1 < QUESTIONS.length) {
        setDirection(1);
        setIdx(idx + 1);
      } else {
        navigate('/processing');
      }
    }, 280);
  };

  const closeLevelUp = () => {
    setShowLevelUp(null);
    if (idx + 1 < QUESTIONS.length) {
      setDirection(1);
      setIdx(idx + 1);
    } else {
      track('quiz_completed');
      navigate('/processing');
    }
  };

  const back = () => {
    if (showLevelUp) return;
    if (idx === 0) return navigate('/intro');
    setDirection(-1);
    setIdx(idx - 1);
  };

  if (!session.player) return null;

  const levelColors = ['#FFD600', '#0055FF', '#00D95F', '#FF5A00'];
  const levelColor = levelColors[current.id - 1];

  return (
    <div className="min-h-screen gradient-bg grain pb-12">
      <BrandBanner compact />
      <div className="sticky top-0 z-30 glass">
        <div className="max-w-2xl mx-auto px-5 py-3">
          <div className="flex items-center justify-between gap-3 mb-2">
            <button onClick={back} data-testid="quiz-back-button" className="flex items-center gap-1.5 text-xs font-bold text-muted-ink hover:text-ink">
              <ArrowLeft className="w-4 h-4" strokeWidth={3} /> Back
            </button>
            <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-muted-ink">
              Level {current.id} · {current.name}
            </div>
            <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-muted-ink" data-testid="quiz-progress-counter">
              {idx + 1} / {QUESTIONS.length}
            </div>
          </div>
          <div className="h-3 w-full bg-[#F1F5F9] rounded-full overflow-hidden">
            <motion.div
              className="h-full rounded-full"
              style={{ background: `linear-gradient(90deg, ${levelColor}, #0055FF)` }}
              initial={{ width: 0 }}
              animate={{ width: `${progress}%` }}
              transition={{ duration: 0.5, ease: 'easeOut' }}
            />
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-5 pt-8 relative">
        <AnimatePresence mode="wait" custom={direction}>
          <motion.div
            key={q.id}
            custom={direction}
            initial={{ opacity: 0, x: direction * 60 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -direction * 60 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
          >
            <p className="text-xs uppercase tracking-[0.2em] font-bold" style={{ color: levelColor }}>Question {q.id}</p>
            <h2 className="font-display font-black text-ink text-2xl sm:text-3xl mt-2 leading-tight tracking-tight" data-testid="quiz-question-text">
              {q.q}
            </h2>

            <div className="grid sm:grid-cols-2 gap-3 sm:gap-4 mt-7">
              {q.options.filter((o) => o.id !== 'E').map((o) => {
                const Icon = Icons[o.icon] || Icons.Sparkles;
                return (
                  <motion.button
                    key={o.id}
                    whileTap={{ scale: 0.97 }}
                    onClick={() => pick(o)}
                    data-testid={`quiz-option-${o.id}`}
                    className="card-soft p-5 sm:p-6 text-left border-2 border-[#E2E8F0] hover:border-[#0055FF] hover:-translate-y-1 transition-all flex items-start gap-3"
                  >
                    <div className="w-10 h-10 rounded-xl bg-[#0055FF]/10 text-[#0055FF] flex items-center justify-center shrink-0">
                      <Icon strokeWidth={2.5} />
                    </div>
                    <div>
                      <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-muted-ink">Option {o.id}</div>
                      <div className="font-display font-extrabold text-ink mt-0.5 text-base sm:text-lg leading-snug">{o.text}</div>
                    </div>
                  </motion.button>
                );
              })}
            </div>

            {/* "None of the above" — full-width, neutral styling */}
            {q.options.find((o) => o.id === 'E') && (
              <motion.button
                whileTap={{ scale: 0.98 }}
                onClick={() => pick(q.options.find((o) => o.id === 'E'))}
                data-testid="quiz-option-E"
                className="w-full mt-3 sm:mt-4 rounded-2xl border-2 border-dashed border-[#CBD5E1] bg-white hover:border-[#0055FF] hover:bg-[#0055FF]/5 transition-all py-4 px-5 flex items-center justify-center gap-2.5 text-muted-ink hover:text-ink"
              >
                <Icons.HelpCircle className="w-5 h-5" strokeWidth={2.5} />
                <span className="font-display font-extrabold text-sm sm:text-base">None of the above</span>
              </motion.button>
            )}
          </motion.div>
        </AnimatePresence>

        <AnimatePresence>
          {pulse && (
            <motion.div
              initial={{ opacity: 0, scale: 0.6 }}
              animate={{ opacity: 1, scale: 1.05 }}
              exit={{ opacity: 0, scale: 1.2 }}
              transition={{ duration: 0.28 }}
              className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-40 pointer-events-none"
            >
              <div className="w-16 h-16 rounded-full bg-white shadow-2xl flex items-center justify-center" style={{ boxShadow: `0 10px 40px ${levelColor}66` }}>
                <CheckCircle2 className="w-8 h-8" style={{ color: levelColor }} strokeWidth={2.5} />
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <AnimatePresence>
        {showLevelUp && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-[#0F172A]/70 backdrop-blur-md flex items-center justify-center p-5"
          >
            <motion.div
              initial={{ scale: 0.7, opacity: 0, y: 30 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              transition={{ type: 'spring', stiffness: 200, damping: 18 }}
              className="card-soft max-w-md w-full p-7 text-center relative overflow-hidden"
              onAnimationComplete={() => fireCenterPop()}
            >
              <div className="absolute inset-x-0 top-0 h-1.5" style={{ background: `linear-gradient(90deg, ${levelColor}, #0055FF)` }} />
              <div className="mx-auto w-24 h-24 rounded-full flex items-center justify-center shadow-lg animate-pulse-glow relative"
                   style={{ background: `linear-gradient(135deg, ${levelColor}, #0070fb)` }}>
                {/* Student silhouette with question-mark badge (shown every 6 questions) */}
                <svg viewBox="0 0 64 64" className="w-14 h-14" fill="none" aria-hidden="true">
                  {/* Graduation cap */}
                  <path d="M32 12 L58 22 L32 32 L6 22 Z" fill="#ffffff" />
                  <rect x="30.5" y="30" width="3" height="8" fill="#ffffff" />
                  <circle cx="34" cy="39" r="1.6" fill="#ffffff" />
                  {/* Head */}
                  <circle cx="32" cy="40" r="8" fill="#ffffff" />
                  {/* Shoulders */}
                  <path d="M16 60 C 18 50, 46 50, 48 60 Z" fill="#ffffff" />
                </svg>
                {/* Question-mark badge overlay */}
                <div className="absolute -bottom-1 -right-1 w-9 h-9 rounded-full flex items-center justify-center font-display font-black text-lg shadow-lg ring-4 ring-white"
                     style={{ background: '#e59501', color: '#0F172A' }}>?</div>
              </div>
              <p className="text-xs uppercase tracking-[0.25em] font-bold mt-5" style={{ color: levelColor }}>
                Level {showLevelUp.id} of 4 complete
              </p>
              <h3 className="font-display font-black text-ink text-2xl sm:text-3xl mt-1 tracking-tight">{showLevelUp.name}</h3>
              <p className="text-body mt-3 text-sm sm:text-base">{showLevelUp.message}</p>
              <button onClick={closeLevelUp} data-testid="continue-after-level" className="btn-primary mt-6 w-full inline-flex items-center justify-center gap-2">
                {showLevelUp.id === 4 ? 'Reveal My Result' : `Start Level ${showLevelUp.id + 1}`}
                <ArrowRight className="w-4 h-4" strokeWidth={3} />
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
